import { CloudArrowUpIcon } from '@phosphor-icons/react';
import { Upload as AntdUpload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import type { UploadFn, UploadVariant, UploadedFile } from '@/lib/upload';
import { UPLOAD_PRESETS, validateFile } from '@/lib/upload';

import { UploadItem, type UploadEntry } from './upload-item';
import styles from './upload.module.scss';

type AntdUploadRequest = { file: File & { uid: string } };

export type { UploadVariant } from '@/lib/upload';

export type UploadProps = {
  variant: UploadVariant;
  onUpload: UploadFn;
  value?: UploadedFile[];
  onChange?: (files: UploadedFile[]) => void;
  maxCount?: number;
  maxSizeMb?: number;
  accept?: string;
  disabled?: boolean;
  helpText?: ReactNode;
  status?: 'error';
  id?: string;
};

type Entry = UploadEntry & { raw?: File; abort?: () => void; committed?: UploadedFile };

const DEFAULT_HELP: Record<UploadVariant, string> = {
  image: UPLOAD_PRESETS.componentImages.helpText,
  document: 'Add a video or upload a PDF/DOC file to help users\nwith installation.',
};

function makePreview(file: File): string | undefined {
  return typeof URL?.createObjectURL === 'function' ? URL.createObjectURL(file) : undefined;
}

export function Upload({
  variant,
  onUpload,
  value,
  onChange,
  maxCount,
  maxSizeMb,
  accept,
  disabled,
  helpText,
  status,
  id,
}: UploadProps) {
  const [entries, setEntriesState] = useState<Entry[]>(() =>
    (value ?? []).map((file) => ({
      uid: file.id,
      name: file.name,
      size: file.size,
      status: 'success' as const,
      percent: 100,
      url: file.url,
      committed: file,
    })),
  );
  const entriesRef = useRef(entries);
  const setEntries = (next: Entry[] | ((prev: Entry[]) => Entry[])) => {
    const resolved = typeof next === 'function' ? next(entriesRef.current) : next;
    entriesRef.current = resolved;
    setEntriesState(resolved);
  };

  const emit = (next: Entry[]) => {
    onChange?.(next.filter((e) => e.status === 'success' && e.committed).map((e) => e.committed!));
  };

  const patch = (uid: string, changes: Partial<Entry>, andEmit = false) => {
    if (andEmit) {
      const next = entriesRef.current.map((e) => (e.uid === uid ? { ...e, ...changes } : e));
      setEntries(next);
      emit(next);
    } else {
      setEntries((prev) => prev.map((e) => (e.uid === uid ? { ...e, ...changes } : e)));
    }
  };

  const runUpload = (file: File, uid: string) => {
    const controller = new AbortController();
    patch(uid, {
      status: 'uploading',
      percent: 0,
      error: undefined,
      abort: () => controller.abort(),
    });
    onUpload(file, {
      onProgress: (percent) => patch(uid, { percent }),
      signal: controller.signal,
    })
      .then((uploaded) =>
        patch(
          uid,
          {
            status: 'success',
            percent: 100,
            url: uploaded.url,
            name: uploaded.name,
            committed: uploaded,
          },
          true,
        ),
      )
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        patch(uid, {
          status: 'error',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
      });
  };

  const handleRequest = (options: AntdUploadRequest) => {
    const file = options.file;
    setEntries((prev) => [
      ...prev,
      {
        uid: file.uid,
        name: file.name,
        size: file.size,
        status: 'uploading',
        percent: 0,
        raw: file,
        retriable: true,
        preview: variant === 'image' ? makePreview(file) : undefined,
      },
    ]);
    runUpload(file, file.uid);
  };

  const pushError = (file: RcFile, error: string) => {
    setEntries((prev) => [
      ...prev,
      { uid: file.uid, name: file.name, size: file.size, status: 'error', percent: 0, error },
    ]);
  };

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    const err = validateFile(file, { accept, maxSizeMb });
    if (err) {
      pushError(file, err);
      return AntdUpload.LIST_IGNORE;
    }
    if (maxCount) {
      const batchIndex = fileList.indexOf(file);
      if (entriesRef.current.length + batchIndex >= maxCount) {
        pushError(file, `Maximum ${maxCount} files allowed`);
        return AntdUpload.LIST_IGNORE;
      }
    }
    return true;
  };

  const handleRemove = (uid: string) => {
    const target = entriesRef.current.find((e) => e.uid === uid);
    target?.abort?.();
    if (target?.preview) URL.revokeObjectURL(target.preview);
    const next = entriesRef.current.filter((e) => e.uid !== uid);
    setEntries(next);
    emit(next);
  };

  const handleRetry = (uid: string) => {
    const target = entriesRef.current.find((e) => e.uid === uid);
    if (target?.raw) runUpload(target.raw, uid);
  };

  const atMax = maxCount ? entries.length >= maxCount : false;

  return (
    <div className={styles.root} id={id}>
      <AntdUpload.Dragger
        multiple
        accept={accept}
        showUploadList={false}
        disabled={disabled || atMax}
        beforeUpload={beforeUpload}
        customRequest={handleRequest as (options: object) => void}
        className={[styles.dragger, status === 'error' ? styles.error : undefined]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={styles.empty}>
          <CloudArrowUpIcon size={32} aria-hidden />
          <p className={styles.help}>{helpText ?? DEFAULT_HELP[variant]}</p>
        </div>
      </AntdUpload.Dragger>
      {entries.length > 0 ? (
        <div className={variant === 'image' ? styles.grid : styles.list}>
          {entries.map((entry) => (
            <UploadItem
              key={entry.uid}
              variant={variant}
              entry={entry}
              onRemove={handleRemove}
              onRetry={handleRetry}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
